import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminStatistics, getAccessRequests } from '../services/api';
import './AdminOverview.css';

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const [statsRes, requestsRes] = await Promise.all([
          getAdminStatistics('30'),
          getAccessRequests().catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setPendingRequests((requestsRes.data || []).filter(r => r.status === 'pending').length);
      } catch (err) {
        console.error('Error fetching admin overview:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="admin-overview">
        <div className="spinner" />
      </div>
    );
  }

  const overview = stats?.overview || stats || {};
  const growth = stats?.growth || [];
  const maxGrowth = Math.max(...growth.map((g) => g.count), 1);

  const cards = [
    {
      to: '/admin/users',
      title: 'Users',
      description: 'Create, edit, and manage user accounts, roles, and repo access.',
      stat: overview.totalUsers ?? '—',
      statLabel: 'Total users',
      icon: '👥',
    },
    {
      to: '/admin/repositories',
      title: 'Repositories',
      description: 'View and manage repository visibility and access.',
      icon: '📦',
    },
    {
      to: '/admin/access-requests',
      title: 'Access Requests',
      description: 'Review and approve or reject access requests.',
      stat: pendingRequests,
      statLabel: 'Pending',
      icon: '🔐',
    },
    {
      to: '/admin/activity-logs',
      title: 'Activity Logs',
      description: 'Audit trail of admin actions and security events.',
      icon: '📋',
    },
    {
      to: '/admin/access-matrix',
      title: 'Access Matrix',
      description: 'View who has access to which repositories.',
      icon: '🗂️',
    },
  ];

  return (
    <div className="admin-overview">
      <h1 className="admin-overview-title">Admin overview</h1>
      <p className="admin-overview-desc">Manage users, repositories, and access from one place.</p>

      <div className="admin-overview-cards">
        {cards.map((card) => (
          <Link key={card.to} to={card.to} className="admin-overview-card">
            <span className="admin-overview-card-icon">{card.icon}</span>
            <div className="admin-overview-card-body">
              <h2 className="admin-overview-card-title">{card.title}</h2>
              <p className="admin-overview-card-desc">{card.description}</p>
              {card.stat !== undefined && (
                <div className="admin-overview-card-stat">
                  <span className="admin-overview-card-stat-value">{card.stat}</span>
                  <span className="admin-overview-card-stat-label">{card.statLabel}</span>
                </div>
              )}
            </div>
            <span className="admin-overview-card-arrow">→</span>
          </Link>
        ))}
      </div>

      {overview && (
        <section className="admin-overview-stats">
          <h2 className="admin-overview-section-title">User statistics (last 30 days)</h2>
          <div className="admin-overview-stats-grid">
            <div className="admin-overview-stat">
              <span className="admin-overview-stat-value">{overview.activeUsers ?? '—'}</span>
              <span className="admin-overview-stat-label">Active users</span>
            </div>
            <div className="admin-overview-stat">
              <span className="admin-overview-stat-value">{overview.newUsers ?? '—'}</span>
              <span className="admin-overview-stat-label">New users</span>
            </div>
            <div className="admin-overview-stat">
              <span className="admin-overview-stat-value">{overview.mfaEnabled ?? '—'}</span>
              <span className="admin-overview-stat-label">MFA enabled</span>
            </div>
            <div className="admin-overview-stat">
              <span className="admin-overview-stat-value">{overview.mustChangePassword ?? '—'}</span>
              <span className="admin-overview-stat-label">Must change password</span>
            </div>
            <div className="admin-overview-stat">
              <span className="admin-overview-stat-value">{overview.expiredPasswords ?? '—'}</span>
              <span className="admin-overview-stat-label">Expired passwords</span>
            </div>
          </div>
        </section>
      )}

      {growth.length > 0 && (
        <section className="admin-overview-growth">
          <h2 className="admin-overview-section-title">User growth (30 days)</h2>
          <div className="growth-chart">
            {growth.map((day) => (
              <div key={day.date} className="growth-bar-wrap" title={`${day.date}: ${day.count} new users`}>
                <div
                  className="growth-bar"
                  style={{ height: `${Math.max(4, (day.count / maxGrowth) * 100)}%` }}
                />
                <span className="growth-label">{day.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminOverview;
